import { createContext } from 'react';
import { Site } from '../components/common/CommonTypes';
import { Journey } from '../consts/Common';

export type SiteState = {
  site: Site;
};

export type SiteContextState = {
  site: Site;
  setSite: (site: Site) => void;
};

export const initialSite: Site = {
  user: {
    instanceId: '',
    token: '',
    userId: '',
    dealerCode: '',
    isValid: false,
    error: '',
    recaptchaSiteKey: '',
    isDealer: false
  },
  brand: {
    brandName: '',
    brandStyles: '',
    googleTagManagerId: '',
    worldPay: false,
    openSiteDealerCode: '',
    brandDisplayName: '',
    brandDisplayFullName: '',
    brandPhoneNumbers: {
      quotesAndRenewalsNumber: '',
      changesAndComplaintsNumber: '',
      claimsNumber: '',
      excessReturnVoucherNumber: '',
      glassHelplineNumber: '',
      lostKeysNumber: '',
      breakdownAssistanceNumber: '',
      europeanBreakdownAssistanceNumber: ''
    },
    useChangesNumberOnPolicyDocsPage: false,
    brandVanityUrl: '',
    brandAddress: '',
    tiaProductCode: '',
    showInsuranceVendorDropdown: true,
    showAssistanceLink: true,
    useAlternateEligibility: false
  },
  content: {
    productPage: {
      productTypeCustomisation: [],
      title: '',
      description: '',
      information: '',
      warning: '',
      justSoYouKnow: '',
      noProductsFound: ''
    },
    productVariantPage: {
      title: '',
      description: '',
      information: '',
      warning: '',
      justSoYouKnow: '',
      disclaimer: '',
      paymentProfileLabel: '',
      manufacturersWarrantyDuration: 0
    },
    landingPage: {
      backgroundImageUrl: '',
      title: '',
      description: '',
      information: '',
      warning: '',
      videoUrl: '',
      journeyTimeNote: '',
      useVinInput: false,
      forceVinCheck: false,
      vinNotFoundText: '',
      whySection: {
        whySectionHeader: '',
        whySectionDescription: '',
        whySectionLists: [
          {
            whySectionListHeader: '',
            caveatText: '',
            whySectionListItems: [
              {
                whySectionItemTitle: '',
                whySectionItemDescription: '',
                whySectionItemLogoUrl: ''
              }
            ]
          }
        ]
      },
      anyQuestionsSection: {
        landingPageQuestionsSectionHeader: '',
        landingPageQuestionsSectionDescription: '',
        landingPageQuestionsSectionNumber: '',
        landingPageQuestionsSectionEmail: '',
        openingHours: [
          {
            openingDays: '',
            openingTimes: '',
            availableServices: ''
          },
          {
            openingDays: '',
            openingTimes: '',
            availableServices: ''
          }
        ]
      },
      videoAlternativeImageUrl: '',
      wistiaEmbedUrl: ''
    },
    vehiclePage: {
      title: '',
      description: '',
      information: '',
      warning: '',
      justSoYouKnow: '',
      upsellAnnualMileage: '',
      hideDealerReference: false,
      hidePurchasePrice: false,
      hideDeliveryDate: false,
      hideMileage: false,
      editablePurchasePrice: false,
      editableMileage: false,
      hideEstimatedDeliveryDate: false,
      mandatorySalesId: false,
      externalReferenceLabelText: '',
      hideVinInput: false
    },
    customerPage: {
      title: '',
      description: '',
      information: '',
      warning: '',
      justSoYouKnow: '',
      isDateOfBirthOptional: false,
      phoneNumberLabel: ''
    },
    summaryPage: {
      title: '',
      description: '',
      information: '',
      warning: '',
      justSoYouKnow: '',
      acceptanceText: [],
      acceptanceTickboxText: '',
      consentToRenewalMarketingTickboxText: ''
    },
    directDebitPage: {
      guaranteePayee: '',
      title: '',
      description: '',
      information: '',
      warning: '',
      justSoYouKnow: ''
    },
    singlePaymentPage: {
      title: '',
      description: '',
      information: '',
      warning: '',
      subscriptionInformation: '',
      justSoYouKnow: ''
    },
    confirmationPage: {
      title: '',
      description: '',
      information: '',
      warning: '',
      summaryDescription: '',
      emailConfirmation: '',
      backgroundImageUrl: ''
    },
    common: {
      navLogoUrl: '',
      faviconUrl: '',
      navMobileLogoUrl: '',
      hideCustomerPlan: false
    },
    footer: {
      contactDetailItems: {
        title: '',
        items: []
      },
      legalDocumentsItems: {
        title: '',
        items: []
      },
      tradingNameItems: {
        title: '',
        items: []
      }
    },
    reasonsToProtect: {
      title: '',
      items: []
    },
    reasonsToExtend: {
      title: '',
      items: []
    },
    usefulDocuments: [],
    quotePage: {
      title: '',
      description: '',
      information: '',
      warning: '',
      justSoYouKnow: '',
      selectionText: '',
      productTypeCustomisations: []
    },
    force24Script: '',
    allureScript: '',
    hideLateRenewalPage: false
  },
  sagePayBaseUrl: '',
  journeyType: Journey.Initial,
  recordId: '',
  renewalSourceSystem: '',
  includeQuotes: false,
  includeOpenSite: false,
  isAppUnderMaintenance: false,
  maintenanceMessage: '',
  countryCode: '',
  cultureCode: 'en-gb',
  quoteUpsell: false,
  tiaData: '',
  isInsuranceMobilise: false,
  vPol: ''
};

export const initialSiteContext: SiteContextState = {
  site: initialSite,
  setSite: () => undefined
};

export const SiteContext = createContext<SiteContextState>(initialSiteContext);
